import { communityAdminApis } from '@/api/admin/community-admin';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import type { IThunkAPI } from '@/store/types';
import type { IBlog, ICommunityAdminInitialState, ICommunityConfiguration, IFAQ, ITestimonial } from '../types/types';
import { IGetCommunityRequest, IReorderCommunityRequest } from '@/api/types';

export const initialState: ICommunityAdminInitialState = {
  configValues: undefined,
  countriesList: [],
};

// get configuration
export const getConfiguration = createAsyncThunk<any, void, IThunkAPI>(
  'community/configuration',
  async (_, thunkAPI) => {
    try {
      const response = await communityAdminApis.getCommunityPageConfigValues();
      const config: ICommunityConfiguration = response.data;
      return config;
    } catch (e) {
      return thunkAPI.rejectWithValue(e.response.data);
    }
  },
);

// list community blogs
export const getCommunityBlogList = createAsyncThunk<any, IGetCommunityRequest, IThunkAPI>(
  'community/blogs',
  async (payload, thunkAPI) => {
    try {
      const response = await communityAdminApis.getBlogs(payload);

      return response.data;
    } catch (e) {
      return thunkAPI.rejectWithValue(e.response.data);
    }
  },
);

// list esign documents
export const getCommunityFaqList = createAsyncThunk<any, IGetCommunityRequest, IThunkAPI>(
  'community/faqs',
  async (payload, thunkAPI) => {
    try {
      const response = await communityAdminApis.getFaqs(payload);

      return response.data;
    } catch (e) {
      return thunkAPI.rejectWithValue(e.response.data);
    }
  },
);

// list esign documents
export const getCommunityTestimonialList = createAsyncThunk<any, IGetCommunityRequest, IThunkAPI>(
  'community/testimonials',
  async (payload, thunkAPI) => {
    try {
      const response = await communityAdminApis.getTestimonials(payload);

      return response.data;
    } catch (e) {
      return thunkAPI.rejectWithValue(e.response.data);
    }
  },
);

export const saveBlog = createAsyncThunk<any, IBlog, IThunkAPI>('community/blog/save', async (blogPost, thunkAPI) => {
  try {
    const response = await communityAdminApis.saveBlog(blogPost);
    return response.data;
  } catch (e) {
    return thunkAPI.rejectWithValue(e.response.data);
  }
});

export const saveFaq = createAsyncThunk<any, IFAQ, IThunkAPI>('community/faq/save', async (faq, thunkAPI) => {
  try {
    const response = await communityAdminApis.saveFaq(faq);
    return response.data;
  } catch (e) {
    return thunkAPI.rejectWithValue(e.response.data);
  }
});

export const saveTestimonial = createAsyncThunk<any, ITestimonial, IThunkAPI>(
  'community/testimonial/save',
  async (testimonial, thunkAPI) => {
    try {
      const response = await communityAdminApis.saveTestimonial(testimonial);
      return response.data;
    } catch (e) {
      return thunkAPI.rejectWithValue(e.response.data);
    }
  },
);

export const getTestimonialById = createAsyncThunk<any, number, IThunkAPI>(
  '/community/testimonial/',
  async (testimonialId, thunkAPI) => {
    try {
      const response = await communityAdminApis.getTestimonialById(testimonialId);
      return response.data;
    } catch (e) {
      return thunkAPI.rejectWithValue(e.response.data);
    }
  },
);

export const getBlogById = createAsyncThunk<any, number, IThunkAPI>('/community/blog/', async (blogId, thunkAPI) => {
  try {
    const response = await communityAdminApis.getBlogById(blogId);
    return response.data;
  } catch (e) {
    return thunkAPI.rejectWithValue(e.response.data);
  }
});

export const getFaqById = createAsyncThunk<any, number, IThunkAPI>('/community/faq/', async (faqId, thunkAPI) => {
  try {
    const response = await communityAdminApis.getFaqById(faqId);
    return response.data;
  } catch (e) {
    return thunkAPI.rejectWithValue(e.response.data);
  }
});

// archive community
export const archiveCommunity = createAsyncThunk<any, number, IThunkAPI>('community/archive', async (id, thunkAPI) => {
  try {
    const response = await communityAdminApis.archiveCommunity(id);

    return response.data;
  } catch (e) {
    return thunkAPI.rejectWithValue(e.response.data);
  }
});

// unarchive community
export const unarchiveCommunity = createAsyncThunk<any, number, IThunkAPI>(
  'community/unarchive',
  async (id, thunkAPI) => {
    try {
      const response = await communityAdminApis.unarchiveCommunity(id);

      return response.data;
    } catch (e) {
      return thunkAPI.rejectWithValue(e.response.data);
    }
  },
);

// reorder community
export const reorderCommunity = createAsyncThunk<any, IReorderCommunityRequest, IThunkAPI>(
  'community/reorder',
  async (payload, thunkAPI) => {
    try {
      const response = await communityAdminApis.reorderCommunity(payload);

      return response.data;
    } catch (e) {
      return thunkAPI.rejectWithValue(e.response.data);
    }
  },
);

export const getCountriesListByClientId = createAsyncThunk<any, number, IThunkAPI>(
  'community/countries',
  async (id, thunkAPI) => {
    try {
      const response = await communityAdminApis.getCountriesListByClientId(id);

      return response.data;
    } catch (e) {
      return thunkAPI.rejectWithValue(e.response.data);
    }
  },
);

export const communityAdminSlice = createSlice({
  name: 'communityAdmin',
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder.addCase(getConfiguration.fulfilled, (state, action) => {
      state.configValues = action.payload;
    });
    builder.addCase(getCountriesListByClientId.fulfilled, (state, action) => {
      state.countriesList = action.payload;
    });
  },
});

export default communityAdminSlice.reducer;
